// Convert hex to HSL for better color analysis
const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  // Remove the hash if present
  hex = hex.replace(/^#/, "");

  // Ensure hex string is valid and has at least 6 characters
  if (hex.length < 6) {
    console.warn(`Invalid hex string: ${hex}, padding with zeros`);
    hex = hex.padEnd(6, "0");
  }

  // Parse the hex values with error handling
  const r = (parseInt(hex.slice(0, 2), 16) || 0) / 255;
  const g = (parseInt(hex.slice(2, 4), 16) || 0) / 255;
  const b = (parseInt(hex.slice(4, 6), 16) || 0) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  return {
    h: h * 360,
    s: s * 100,
    l: l * 100,
  };
};

// Convert RGB values to hex string
const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };
  return "#" + toHex(r) + toHex(g) + toHex(b);
};

// Convert hex to RGB for better white detection
const hexToRGB = (hex: string): { r: number; g: number; b: number } => {
  // Debug logging for problematic colors
  if (hex.includes("NaN") || hex.includes("undefined") || !hex) {
    console.warn(`🚨 Problematic hex string detected: "${hex}"`);
  }

  // Remove the hash if present
  hex = hex.replace(/^#/, "");

  // Ensure hex string is valid and has at least 6 characters
  if (hex.length < 6) {
    console.warn(`Invalid hex string: ${hex}, padding with zeros`);
    hex = hex.padEnd(6, "0");
  }

  // Parse each component with error handling
  const r = parseInt(hex.slice(0, 2), 16) || 0;
  const g = parseInt(hex.slice(2, 4), 16) || 0;
  const b = parseInt(hex.slice(4, 6), 16) || 0;

  // Validate that all values are valid numbers
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    console.warn(`Invalid RGB values parsed from hex: ${hex}, using fallback`);
    return { r: 128, g: 128, b: 128 }; // Fallback to gray
  }

  return { r, g, b };
};

// Calculate color distance in RGB space
const getRGBDistance = (hex1: string, hex2: string): number => {
  const rgb1 = hexToRGB(hex1);
  const rgb2 = hexToRGB(hex2);

  // Calculate Euclidean distance in RGB space
  const rDiff = rgb1.r - rgb2.r;
  const gDiff = rgb1.g - rgb2.g;
  const bDiff = rgb1.b - rgb2.b;

  // Normalize to 0-1 range
  return Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff) / 441.67; // max possible distance
};

// Calculate perceived brightness of a color using relative luminance formula
const getPerceivedBrightness = (rgb: {
  r: number;
  g: number;
  b: number;
}): number => {
  // Using relative luminance formula (Rec. 709)
  // L = 0.2126 * R + 0.7152 * G + 0.0722 * B
  return (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
};

// Calculate contrast ratio between two colors
const getContrastRatio = (
  rgb1: { r: number; g: number; b: number },
  rgb2: { r: number; g: number; b: number }
): number => {
  const l1 = getPerceivedBrightness(rgb1);
  const l2 = getPerceivedBrightness(rgb2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

// Check if colors are complementary
const areComplementary = (color1: string, color2: string): boolean => {
  const rgb1 = hexToRGB(color1);
  const rgb2 = hexToRGB(color2);

  // Calculate brightness difference
  const brightness1 = getPerceivedBrightness(rgb1);
  const brightness2 = getPerceivedBrightness(rgb2);
  const brightnessDiff = Math.abs(brightness1 - brightness2);

  // Calculate color distance
  const colorDistance = getRGBDistance(color1, color2);

  // Colors are complementary if they have significant brightness difference
  // or if they're far apart in RGB space
  return brightnessDiff > 0.5 || colorDistance > 0.7;
};

// Check if colors are analogous
const areAnalogous = (color1: string, color2: string): boolean => {
  const rgb1 = hexToRGB(color1);
  const rgb2 = hexToRGB(color2);

  // Calculate normalized differences in each channel
  const rDiff = Math.abs(rgb1.r - rgb2.r) / 255;
  const gDiff = Math.abs(rgb1.g - rgb2.g) / 255;
  const bDiff = Math.abs(rgb1.b - rgb2.b) / 255;

  // Colors are analogous if their RGB values are relatively close
  const maxChannelDiff = Math.max(rDiff, gDiff, bDiff);
  return maxChannelDiff < 0.3;
};

// Check if colors are triadic (form a triangle on the color wheel)
const areTriadic = (color1: string, color2: string): boolean => {
  const hsl1 = hexToHSL(color1);
  const hsl2 = hexToHSL(color2);

  // Neutral colors don't form triadic relationships
  if (isNeutral(color1) || isNeutral(color2)) {
    return false;
  }

  const hueDiff = Math.abs(hsl1.h - hsl2.h);
  return Math.abs(hueDiff - 120) <= 30; // Within 30 degrees of 120
};

// Check if colors are monochromatic (same hue, different brightness/saturation)
const areMonochromatic = (
  color1: string,
  color2: string,
  isMajorPair: boolean = false
): boolean => {
  // Get HSL values for better hue comparison
  const hsl1 = hexToHSL(color1);
  const hsl2 = hexToHSL(color2);

  // If both colors are neutral, handle specially
  if (isNeutral(color1) && isNeutral(color2)) {
    // For major pairs of neutral colors, we want to be more strict
    if (isMajorPair) {
      // Black and white combinations are high contrast, not monochromatic
      if (
        (isBlack(color1) && isWhite(color2)) ||
        (isBlack(color2) && isWhite(color1))
      ) {
        return false;
      }
      // Same color combinations (black+black, white+white, gray+gray) are monochromatic
      if (color1 === color2) {
        return true;
      }
      // Check for very high contrast between dark and light neutrals
      const rgb1 = hexToRGB(color1);
      const rgb2 = hexToRGB(color2);
      const brightness1 = (rgb1.r + rgb1.g + rgb1.b) / 3;
      const brightness2 = (rgb2.r + rgb2.g + rgb2.b) / 3;
      const brightnessDiff = Math.abs(brightness1 - brightness2);

      // If the brightness difference is very large (> 150), treat as high contrast
      if (brightnessDiff > 150) {
        return false;
      }

      // Allow black with any other neutral (except white, which we handled above)
      if (isBlack(color1) || isBlack(color2)) {
        return false;
      }
      // For other neutral pairs in major garments, consider them monochromatic
      // This will trigger the monochromatic penalty for neutral-neutral major pairs
      return true;
    }
    // For non-major pairs (like accessories), neutrals are fine together
    return false;
  }

  // Calculate differences
  const hueDiff = Math.abs(hsl1.h - hsl2.h);
  const satDiff = Math.abs(hsl1.s - hsl2.s);
  const lightDiff = Math.abs(hsl1.l - hsl2.l);

  // Stricter thresholds for major pairs (top-bottom combinations)
  const hueThreshold = isMajorPair ? 5 : 10;
  const satThreshold = isMajorPair ? 20 : 30;
  const lightThreshold = isMajorPair ? 30 : 50;

  // For major pairs, require more difference in lightness to not be considered monochromatic
  const isMonochromatic = hueDiff <= hueThreshold && satDiff <= satThreshold;
  if (isMajorPair) {
    // For major pairs, if the colors are too similar in all aspects, they're monochromatic
    if (isMonochromatic && lightDiff < lightThreshold) {
      return true;
    }
    // If they have very different lightness, they're not monochromatic even with same hue
    return false;
  }

  return isMonochromatic;
};

// Check if a color is black (or very close to black)
const isBlack = (hex: string): boolean => {
  const rgb = hexToRGB(hex);
  // More lenient: consider very dark colors (RGB values ≤ 40) as black
  return rgb.r <= 40 && rgb.g <= 40 && rgb.b <= 40;
};

// Check if a color is white (or very close to white)
const isWhite = (hex: string): boolean => {
  const rgb = hexToRGB(hex);
  // More lenient: consider very light colors (RGB values ≥ 220) as white
  return rgb.r >= 220 && rgb.g >= 220 && rgb.b >= 220;
};

// Check if colors are neutral (including white, black, grays, and desaturated colors)
const isNeutral = (color: string): boolean => {
  const hsl = hexToHSL(color);
  const rgb = hexToRGB(color);

  // Black is neutral
  if (isBlack(color)) {
    return true;
  }

  // White check (high RGB values that are close to each other)
  const isWhite =
    rgb.r >= 225 &&
    rgb.g >= 225 &&
    rgb.b >= 225 &&
    Math.abs(rgb.r - rgb.g) <= 15 &&
    Math.abs(rgb.g - rgb.b) <= 15 &&
    Math.abs(rgb.r - rgb.b) <= 15;

  if (isWhite) {
    return true;
  }

  // Colors with very low saturation are neutral
  if (hsl.s <= 15) {
    return true;
  }

  // Colors with both low saturation and extreme lightness are neutral
  if (hsl.s <= 30 && (hsl.l <= 20 || hsl.l >= 80)) {
    return true;
  }

  return false;
};

// Check if colors are high-energy
const isHighEnergy = (hsl: { s: number; l: number }): boolean => {
  return hsl.s >= 80 && hsl.l >= 50; // Highly saturated and bright colors
};

// Helper function to check if colors are similar enough to be considered the same
const areSimilarColors = (
  hsl1: { h: number; s: number; l: number },
  hsl2: { h: number; s: number; l: number }
): boolean => {
  const hueDiff = Math.abs(hsl1.h - hsl2.h);
  const satDiff = Math.abs(hsl1.s - hsl2.s);
  const lightDiff = Math.abs(hsl1.l - hsl2.l);

  // More lenient thresholds for similar colors
  return hueDiff <= 15 && satDiff <= 20 && lightDiff <= 25;
};

// Check if colors form a monochrome outfit with context awareness
export const isMonochromaticOutfit = (
  colors: string[],
  items?: { type: string; occasions?: string[] }[]
): {
  isMonochrome: boolean;
  isAllBlack: boolean;
  isAllWhite: boolean;
  monochromeStrength: "strict" | "partial" | "none";
} => {
  if (colors.length < 2)
    return {
      isMonochrome: false,
      isAllBlack: false,
      isAllWhite: false,
      monochromeStrength: "none",
    };

  // Check for monochromatic combinations
  const similarityChecks = colors.map((color, index) => {
    if (index === 0) return false; // Skip comparing first color with itself

    // Compare with first color
    return areMonochromatic(color, colors[0]);
  });

  const similarColorsCount = similarityChecks.filter(Boolean).length;
  const monochromeRatio = similarColorsCount / (colors.length - 1);

  // Check if it's an all-black or all-white outfit
  const isAllBlack = colors.every((color) => isBlack(color));
  const isAllWhite = colors.every((color) => {
    const rgb = hexToRGB(color);
    return (
      rgb.r >= 225 &&
      rgb.g >= 225 &&
      rgb.b >= 225 &&
      Math.abs(rgb.r - rgb.g) <= 15 &&
      Math.abs(rgb.g - rgb.b) <= 15 &&
      Math.abs(rgb.r - rgb.b) <= 15
    );
  });

  return {
    isMonochrome: monochromeRatio > 0.7,
    isAllBlack,
    isAllWhite,
    monochromeStrength:
      monochromeRatio > 0.7
        ? "strict"
        : monochromeRatio > 0.5
        ? "partial"
        : "none",
  };
};

// Count high-energy colors in an outfit
const countHighEnergyColors = (colors: string[]): number => {
  return colors.map(hexToHSL).filter(isHighEnergy).length;
};

// Helper function to check if a color is in the blue family
const isBlue = (hsl: { h: number; s: number; l: number }): boolean => {
  // Blue hues range from about 180 to 250
  return hsl.h >= 180 && hsl.h <= 250 && hsl.s >= 20;
};

// Helper function to check if a color is in the brown family
const isBrown = (hsl: { h: number; s: number; l: number }): boolean => {
  // Brown is typically orange/red hues with lower lightness
  const isInHueRange =
    (hsl.h >= 20 && hsl.h <= 50) || // Orange-brown
    (hsl.h >= 0 && hsl.h <= 30); // Red-brown
  return (
    isInHueRange &&
    hsl.l >= 15 &&
    hsl.l <= 50 && // Not too dark or light
    hsl.s >= 20 &&
    hsl.s <= 80
  ); // Some saturation but not too vibrant
};

// Convert HSL to RGB
const hslToRgb = (
  h: number,
  s: number,
  l: number
): { r: number; g: number; b: number } => {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return {
    r: 255 * f(0),
    g: 255 * f(8),
    b: 255 * f(4),
  };
};

// Convert HSL object to hex string
const hslToHex = (hsl: { h: number; s: number; l: number }): string => {
  const rgb = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
};

// Helper function to get color wheel relationship type
const getColorWheelRelationship = (
  hsl1: { h: number; s: number; l: number },
  hsl2: { h: number; s: number; l: number }
): {
  type:
    | "complementary"
    | "analogous"
    | "triadic"
    | "split-complementary"
    | "neutral"
    | "none";
  strength: number;
} => {
  // If either color is neutral (very low saturation or very high/low lightness), return neutral
  if (
    hsl1.s < 15 ||
    hsl2.s < 15 ||
    hsl1.l < 10 ||
    hsl1.l > 95 ||
    hsl2.l < 10 ||
    hsl2.l > 95
  ) {
    return { type: "neutral", strength: 1.0 };
  }

  const hueDiff = Math.abs(hsl1.h - hsl2.h);

  // Complementary: Colors opposite on the wheel (180° ± 15°)
  if (Math.abs(hueDiff - 180) <= 15) {
    return { type: "complementary", strength: 1.0 };
  }

  // Split-complementary: Colors 150-165° apart
  if (Math.abs(hueDiff - 150) <= 15 || Math.abs(hueDiff - 210) <= 15) {
    return { type: "split-complementary", strength: 0.9 };
  }

  // Triadic: Colors 120° ± 15° apart
  if (Math.abs(hueDiff - 120) <= 15) {
    return { type: "triadic", strength: 0.85 };
  }

  // Analogous: Colors within 30° of each other
  if (hueDiff <= 30) {
    return { type: "analogous", strength: 0.8 };
  }

  return { type: "none", strength: 0.5 };
};

export const getColorCompatibilityScore = (
  colors1: string[],
  colors2: string[],
  items?: { type: string; occasions?: string[] }[]
): ColorCompatibilityResult => {
  let baseScore = 0;
  let totalWeight = 0;
  let score = 0;
  const explanation: string[] = [];
  const penalties: { type: string; impact: number; description: string }[] = [];
  const foundRelationships = new Set<string>();
  let hasMajorMonochrome = false;
  let monochromaticPairCount = 0;

  if (!colors1.length || !colors2.length) {
    return {
      score: 1,
      explanation: ["No colors to compare"],
      details: {
        baseScore: 1,
        totalPairs: 0,
        penalties: [],
      },
    };
  }

  // First pass: identify monochromatic major pairs
  colors1.forEach((color1, i) => {
    colors2.forEach((color2, j) => {
      // Use the same logic as the main scoring loop
      let type1, type2;

      if (items && items.length === 2) {
        // Case 1: Direct item comparison (outfit builder)
        type1 = items[0]?.type;
        type2 = items[1]?.type;
      } else if (items && items.length > 2) {
        // Case 2: getColorCompatibility case
        const newItemType = items[items.length - 1]?.type;
        type1 = "existing"; // placeholder for existing outfit
        type2 = newItemType; // the new item being added
      } else {
        type1 = items?.[0]?.type;
        type2 = items?.[1]?.type;
      }

      // Only check for monochrome between major garments (top/bottom/dress)
      // Skip monochrome penalty for accessories and shoes
      if (
        isMajorPair(type1, type2) &&
        type1 !== "existing" &&
        type2 !== "shoes" &&
        type2 !== "outerwear"
      ) {
        if (areMonochromatic(color1, color2, true)) {
          hasMajorMonochrome = true;
          monochromaticPairCount++;
        }
      }
    });
  });

  // Calculate base compatibility scores with garment type weighting
  colors1.forEach((color1, i) => {
    colors2.forEach((color2, j) => {
      try {
        // Get garment types - handle different items array structures
        let type1, type2;

        if (items && items.length === 2) {
          // Case 1: Direct item comparison (outfit builder)
          type1 = items[0]?.type;
          type2 = items[1]?.type;
        } else if (items && items.length > 2) {
          // Case 2: getColorCompatibility case
          // colors1 = colors from existing outfit, colors2 = colors from new item
          // The new item is always the last one in the items array
          const newItemType = items[items.length - 1]?.type;

          // For black harmony detection, we need to know if the new item is shoes/outerwear
          // and if it's being compared against existing items
          type1 = "existing"; // Placeholder for existing outfit items
          type2 = newItemType; // The new item being added
        } else {
          type1 = items?.[0]?.type;
          type2 = items?.[1]?.type;
        }

        // Calculate weight based on garment importance
        let weight = 1.0;
        const isMajorPairCombo = isMajorPair(type1, type2);
        const isAccessoryPair =
          getGarmentCategory(type1) === "accessory" ||
          getGarmentCategory(type2) === "accessory" ||
          type2 === "shoes" ||
          type2 === "outerwear";

        if (isMajorPairCombo) {
          weight = 2.0; // Top-bottom combinations matter most
        } else if (isAccessoryPair) {
          // If we have a monochromatic major pair, reduce accessory influence even more
          weight = hasMajorMonochrome ? 0.2 : 0.5;
        } else if (isMajorGarment(type1) || isMajorGarment(type2)) {
          weight = 1.2; // Major garment with non-accessory
        }

        // Convert colors to HSL for color wheel analysis
        const hsl1 = hexToHSL(color1);
        const hsl2 = hexToHSL(color2);

        // Get color wheel relationship
        const relationship = getColorWheelRelationship(hsl1, hsl2);
        let pairScore = relationship.strength;

        // Add relationship to found relationships
        if (relationship.type !== "none") {
          foundRelationships.add(relationship.type);
        }

        // Check for black harmony first (before other neutral handling)
        const isBlackPair = isBlack(color1) && isBlack(color2);
        const isBlackWithNeutral =
          (isBlack(color1) && isNeutral(color2)) ||
          (isBlack(color2) && isNeutral(color1));

        if (
          (isBlackPair || isBlackWithNeutral) &&
          (isAccessoryPair ||
            type1 === "outerwear" ||
            type2 === "outerwear" ||
            type2 === "shoes")
        ) {
          // Black shoes/outerwear with black tops/pants gets high score and higher weight
          pairScore = 1.0;
          weight = 1.5; // Give black harmony pairs higher weight than normal accessories
          foundRelationships.add("black-harmony");
        } else if (
          isNeutral(color1) &&
          isNeutral(color2) &&
          (type2 === "shoes" || type2 === "outerwear" || isAccessoryPair)
        ) {
          // Neutral colors (like tan) with accessories/shoes get good score
          pairScore = 0.8;
          weight = 1.2; // Higher weight than normal accessories
          foundRelationships.add("neutral-harmony");
        } else if (isClassicFashionCombo(color1, color2)) {
          // Classic fashion combinations get bonus points
          pairScore = Math.min(pairScore * 1.3, 1.0);
          weight = 1.3;
          foundRelationships.add("classic-combo");
        } else if (isMonochromaticHarmony(color1, color2)) {
          // Monochromatic combinations get good harmony (different from clashing shades)
          pairScore = Math.min(pairScore * 1.2, 1.0);
          weight = 1.2;
          foundRelationships.add("monochromatic-harmony");
        } else {
          // Special handling for neutral colors (black, white, gray)
          if (relationship.type === "neutral") {
            // Calculate contrast ratio
            const rgb1 = hexToRGB(color1);
            const rgb2 = hexToRGB(color2);
            const contrastRatio = getContrastRatio(rgb1, rgb2);

            // Check if both colors are neutral
            const isNeutralPair =
              (hsl1.s < 15 || hsl1.l > 95 || hsl1.l < 10) &&
              (hsl2.s < 15 || hsl2.l > 95 || hsl2.l < 10);

            if (isNeutralPair) {
              if (contrastRatio >= 4.5) {
                pairScore = 1.0; // High contrast like black/white
                foundRelationships.add("high-contrast");
              } else if (contrastRatio >= 3.0) {
                pairScore = 0.8; // Medium contrast
                foundRelationships.add("medium-contrast");
              } else {
                // Low contrast neutral pairs
                if (isAccessoryPair) {
                  // Accessories can match, but reduce score if we have monochromatic major pieces
                  pairScore = hasMajorMonochrome ? 0.4 : 0.7;
                } else if (isMajorPairCombo) {
                  // Strict with major garments
                  pairScore = 0.3;
                } else {
                  // Major + accessory looks good, but reduce if we have monochromatic major pieces
                  pairScore = hasMajorMonochrome ? 0.5 : 0.85;
                }
              }
            } else {
              // For neutral + non-neutral combinations
              const nonNeutralColor = hsl1.s < 15 ? hsl2 : hsl1;
              const colorStrength =
                (nonNeutralColor.s / 100 +
                  (1 - Math.abs(nonNeutralColor.l - 50) / 50)) /
                2;

              // If we have monochromatic major pieces, reduce the benefit of good neutral+color combinations
              if (hasMajorMonochrome) {
                pairScore = 0.4 + colorStrength * 0.1;
              } else {
                if (contrastRatio >= 3.0) {
                  pairScore = 0.9 + colorStrength * 0.1;
                  foundRelationships.add("high-contrast");
                } else {
                  pairScore = 0.7 + colorStrength * 0.1;
                }
              }
              foundRelationships.add("neutral-accent");
            }
          }
        }

        // Check for strong primary color combinations that should be penalized
        const isStrongPrimary1 = isStrongPrimaryColor(color1);
        const isStrongPrimary2 = isStrongPrimaryColor(color2);

        if (isStrongPrimary1 && isStrongPrimary2) {
          // Both colors are strong primary colors - apply penalty
          const penaltyFactor = 0.3; // Severe penalty for primary + primary
          pairScore = Math.min(pairScore * penaltyFactor, 0.3);
          penalties.push({
            type: "strong-primary-clash",
            impact: -(pairScore * (1 - penaltyFactor)),
            description: `Strong primary colors (${getColorName(
              color1
            )} + ${getColorName(color2)}) create visual conflict`,
          });
          foundRelationships.add("primary-clash");
        } else if (areSimilarShades(color1, color2) && isMajorPairCombo) {
          // Similar shades of the same color in major garments - apply strong penalty
          const penaltyFactor = 0.2; // Very severe penalty for clashing shades
          pairScore = Math.min(pairScore * penaltyFactor, 0.2);
          penalties.push({
            type: "clashing-shades",
            impact: -(pairScore / penaltyFactor - pairScore), // Calculate actual impact
            description: `Similar shades of ${getColorName(
              color1
            )} clash when worn together`,
          });
          foundRelationships.add("clashing-shades");
        } else if (hasMajorMonochrome) {
          // For non-neutral combinations, if we have monochromatic major pieces,
          // reduce the impact of good color combinations
          pairScore = Math.min(pairScore * 0.5, 0.4);
        }

        // Apply weight to the score
        pairScore = Math.max(0, Math.min(pairScore, 1));
        baseScore += pairScore * weight;
        totalWeight += weight;
      } catch (error) {
        // Handle error silently
      }
    });
  });

  // Calculate initial score
  score = totalWeight > 0 ? baseScore / totalWeight : 0;

  // If there's any monochromatic major pair, apply a severe penalty
  if (hasMajorMonochrome) {
    // Check if any of the monochromatic pairs involve stark colors
    let hasStarkMonochromatic = false;
    for (let i = 0; i < colors1.length; i++) {
      for (let j = i + 1; j < colors1.length; j++) {
        const type1 = items?.length === 2 ? items[0]?.type : "existing";
        const type2 =
          items?.length === 2
            ? items[1]?.type
            : items?.[items.length - 1]?.type;

        if (
          isMajorPair(type1, type2) &&
          areMonochromatic(colors1[i], colors1[j])
        ) {
          if (isStarkColor(colors1[i]) || isStarkColor(colors1[j])) {
            hasStarkMonochromatic = true;
            break;
          }
        }
      }
      if (hasStarkMonochromatic) break;
    }

    // Apply stronger penalties for stark colors
    let penaltyFactor, maxScore;
    if (hasStarkMonochromatic) {
      // Much stronger penalty for stark monochromatic outfits
      penaltyFactor = monochromaticPairCount > 1 ? 0.05 : 0.1;
      maxScore = 0.15; // Cap at 15% for stark monochromatic outfits
    } else {
      // Standard penalty for other monochromatic outfits
      penaltyFactor = monochromaticPairCount > 1 ? 0.15 : 0.25;
      maxScore = 0.3; // Cap at 30% for regular monochromatic outfits
    }

    const originalScore = score;
    score = Math.min(score * penaltyFactor, maxScore);

    // Add penalty to details
    const penaltyType = hasStarkMonochromatic
      ? "stark-monochromatic-major-pairs"
      : "monochromatic-major-pairs";
    const penaltyDescription = hasStarkMonochromatic
      ? `Main garments are stark colors in similar shades (${monochromaticPairCount} monochromatic pair${
          monochromaticPairCount > 1 ? "s" : ""
        }) - very bold look`
      : `Main garments are too similar in color (${monochromaticPairCount} monochromatic pair${
          monochromaticPairCount > 1 ? "s" : ""
        })`;

    penalties.push({
      type: penaltyType,
      impact: -(originalScore - score),
      description: penaltyDescription,
    });
  }

  // If there's any penalty, recalculate score with penalties applied
  if (penalties.length > 0) {
    const penaltyImpact = penalties.reduce((sum, p) => sum + p.impact, 0);
    score = Math.max(0, Math.min(score + penaltyImpact, 1));
  }

  // Add explanations based on found relationships
  if (!hasMajorMonochrome) {
    if (foundRelationships.has("complementary")) {
      explanation.push(
        "✨ Contains complementary colors that create strong visual interest"
      );
    }
    if (foundRelationships.has("split-complementary")) {
      explanation.push(
        "✨ Uses split-complementary colors for a sophisticated contrast"
      );
    }
    if (foundRelationships.has("triadic")) {
      explanation.push("✨ Contains triadic color harmony for a vibrant look");
    }
    if (foundRelationships.has("analogous")) {
      explanation.push(
        "✨ Uses analogous colors that create a harmonious flow"
      );
    }
    if (foundRelationships.has("neutral")) {
      explanation.push(
        "👍 Includes neutral colors that help balance the outfit"
      );
    }
    if (foundRelationships.has("black-harmony")) {
      explanation.push(
        "✨ Black shoes/outerwear creates perfect harmony with black tops/pants"
      );
    }
    if (foundRelationships.has("neutral-harmony")) {
      explanation.push(
        "✨ Neutral-colored accessories create harmonious coordination"
      );
    }
    if (foundRelationships.has("classic-combo")) {
      explanation.push(
        "✨ Classic fashion combination that always looks sophisticated"
      );
    }
    if (foundRelationships.has("monochromatic-harmony")) {
      explanation.push(
        "✨ Harmonious monochromatic colors create a cohesive look"
      );
    }
  }

  // Add warnings for problematic combinations
  if (foundRelationships.has("primary-clash")) {
    explanation.push(
      "⚠️ Strong primary colors (red, blue, yellow) clash when paired together",
      "💡 Try pairing primary colors with neutrals or complementary colors instead"
    );
  }
  if (foundRelationships.has("clashing-shades")) {
    explanation.push(
      "⚠️ Similar shades of the same color create visual conflict",
      "💡 Try using contrasting colors or stick to one shade of the color family"
    );
  }

  // Ensure final score is between 0 and 1
  score = Math.max(0, Math.min(score, 1));

  return {
    score,
    explanation,
    details: {
      baseScore: baseScore / totalWeight,
      totalPairs: totalWeight,
      penalties,
    },
  };
};

// Helper function to check if a color is a strong primary color (red, blue, yellow)
const isStrongPrimaryColor = (hex: string): boolean => {
  const hsl = hexToHSL(hex);

  // Must have high saturation to be considered a strong primary color
  if (hsl.s < 60) return false;

  // Must not be too light or too dark
  if (hsl.l < 20 || hsl.l > 80) return false;

  // Check if it's in the primary color ranges
  const hue = hsl.h;

  // Red: 0-30 degrees and 330-360 degrees
  if ((hue >= 0 && hue <= 30) || (hue >= 330 && hue <= 360)) return true;

  // Blue: 210-270 degrees
  if (hue >= 210 && hue <= 270) return true;

  // Yellow: 45-75 degrees
  if (hue >= 45 && hue <= 75) return true;

  return false;
};

// Helper function to check if a color is a strong secondary color (orange, green, purple)
const isStrongSecondaryColor = (hex: string): boolean => {
  const hsl = hexToHSL(hex);

  // Must have high saturation
  if (hsl.s < 60) return false;

  // Must be in mid-range lightness (not too dark or light)
  if (hsl.l < 20 || hsl.l > 80) return false;

  const hue = hsl.h;

  // Orange range
  if (hue >= 10 && hue <= 50) return true;

  // Green range
  if (hue >= 60 && hue <= 180) return true;

  // Purple range
  if (hue >= 270 && hue <= 330) return true;

  return false;
};

// Helper function to check if a color is a stark primary or secondary color
const isStarkColor = (hex: string): boolean => {
  return isStrongPrimaryColor(hex) || isStrongSecondaryColor(hex);
};

// Helper function to check if colors form a classic fashion combination
const isClassicFashionCombo = (color1: string, color2: string): boolean => {
  const hsl1 = hexToHSL(color1);
  const hsl2 = hexToHSL(color2);

  // Navy + Tan/Beige (classic combination)
  const isNavy =
    (hsl1.h >= 200 && hsl1.h <= 280 && hsl1.s > 40 && hsl1.l < 50) ||
    (hsl2.h >= 200 && hsl2.h <= 280 && hsl2.s > 40 && hsl2.l < 50);
  const isTan =
    (hsl1.h >= 30 &&
      hsl1.h <= 60 &&
      hsl1.s > 20 &&
      hsl1.s < 60 &&
      hsl1.l > 60) ||
    (hsl2.h >= 30 && hsl2.h <= 60 && hsl2.s > 20 && hsl2.s < 60 && hsl2.l > 60);

  if (isNavy && isTan) return true;

  // Navy + White (classic)
  const isWhite = (hsl1.l > 90 && hsl1.s < 10) || (hsl2.l > 90 && hsl2.s < 10);
  if (isNavy && isWhite) return true;

  // Charcoal + White (classic)
  const isCharcoal =
    (hsl1.l < 30 && hsl1.s < 20) || (hsl2.l < 30 && hsl2.s < 20);
  if (isCharcoal && isWhite) return true;

  // Complementary colors that work well in fashion
  const hueDiff = Math.abs(hsl1.h - hsl2.h);
  const normalizedHueDiff = Math.min(hueDiff, 360 - hueDiff);

  // Blue + Orange (complementary but fashion-friendly)
  const isBlue =
    (hsl1.h >= 200 && hsl1.h <= 280) || (hsl2.h >= 200 && hsl2.h <= 280);
  const isOrange =
    (hsl1.h >= 10 && hsl1.h <= 50) || (hsl2.h >= 10 && hsl2.h <= 50);
  if (
    isBlue &&
    isOrange &&
    normalizedHueDiff >= 150 &&
    normalizedHueDiff <= 210
  )
    return true;

  return false;
};

// Helper function to check if colors form harmonious monochromatic combinations
const isMonochromaticHarmony = (color1: string, color2: string): boolean => {
  const hsl1 = hexToHSL(color1);
  const hsl2 = hexToHSL(color2);

  // Skip if one is neutral
  if (isNeutral(color1) || isNeutral(color2)) return false;

  // Check if they're in the same color family
  const hueDiff = Math.abs(hsl1.h - hsl2.h);
  const normalizedHueDiff = Math.min(hueDiff, 360 - hueDiff);

  // Same color family if hue difference is small
  const hueThreshold = 30;
  if (normalizedHueDiff > hueThreshold) return false;

  // Check if they have moderate lightness difference (harmonious, not clashing)
  const lightnessDiff = Math.abs(hsl1.l - hsl2.l);

  // Harmonious monochromatic: similar hue, moderate lightness difference, good saturation
  const isHarmonious =
    lightnessDiff >= 15 && lightnessDiff <= 40 && hsl1.s > 30 && hsl2.s > 30;

  return isHarmonious;
};

// Helper function to check if two colors are similar shades of the same color family
const areSimilarShades = (color1: string, color2: string): boolean => {
  const hsl1 = hexToHSL(color1);
  const hsl2 = hexToHSL(color2);

  // Skip neutral colors (black, white, gray)
  if (isNeutral(color1) || isNeutral(color2)) return false;

  // Skip very low saturation colors (they're essentially neutrals)
  if (hsl1.s < 15 || hsl2.s < 15) return false;

  // Check if they're in the same color family (similar hue)
  const hueDiff = Math.abs(hsl1.h - hsl2.h);
  const normalizedHueDiff = Math.min(hueDiff, 360 - hueDiff); // Handle wraparound

  // Very tight hue thresholds for "uncanny valley" clashing shades
  let hueThreshold = 15; // Default threshold - extremely close hues only

  // Slightly more lenient for blues, but still very tight
  const isBlueFamily =
    (hsl1.h >= 180 && hsl1.h <= 300) || (hsl2.h >= 180 && hsl2.h <= 300);
  if (isBlueFamily) hueThreshold = 20; // Very tight for blues

  // Reds/pinks - very tight
  const isRedFamily =
    hsl1.h >= 330 || hsl1.h <= 30 || hsl2.h >= 330 || hsl2.h <= 30;
  if (isRedFamily) hueThreshold = 18; // Very tight for reds

  // Greens - very tight
  const isGreenFamily =
    (hsl1.h >= 60 && hsl1.h <= 180) || (hsl2.h >= 60 && hsl2.h <= 180);
  if (isGreenFamily) hueThreshold = 18; // Very tight for greens

  if (normalizedHueDiff > hueThreshold) return false;

  // Check if they have significantly different lightness (this creates the "clashing shades" effect)
  const lightnessDiff = Math.abs(hsl1.l - hsl2.l);

  // Focus on the "uncanny valley" effect - colors that look like a mistake
  const minLightnessDiff = 8; // Very close lightness
  const maxLightnessDiff = 20; // Not too different - this is the key range
  const minSaturation = 60; // High saturation required

  // Similar shades clash when they have:
  // 1. Same color family (similar hue)
  // 2. Moderately different lightness levels (not too close, not too far)
  // 3. Both have decent saturation (not washed out)
  // 4. Not too extreme a difference (avoid penalizing complementary lightness)
  const isClashingShade =
    lightnessDiff > minLightnessDiff &&
    lightnessDiff < maxLightnessDiff &&
    hsl1.s > minSaturation &&
    hsl2.s > minSaturation;

  return isClashingShade;
};

// Helper function to get a human-readable color name from hex
const getColorName = (hex: string): string => {
  if (isBlack(hex)) return "black";

  const rgb = hexToRGB(hex);
  const isWhite =
    rgb.r >= 225 &&
    rgb.g >= 225 &&
    rgb.b >= 225 &&
    Math.abs(rgb.r - rgb.g) <= 15 &&
    Math.abs(rgb.g - rgb.b) <= 15 &&
    Math.abs(rgb.r - rgb.b) <= 15;
  if (isWhite) return "white";

  const hsl = hexToHSL(hex);
  if (hsl.s < 20) return "gray";
  if (hsl.l < 20) return "dark";
  if (hsl.l > 80) return "light";

  // Basic color wheel segmentation
  const hue = hsl.h;
  if (hue >= 0 && hue < 30) return "red";
  if (hue >= 30 && hue < 60) return "orange";
  if (hue >= 60 && hue < 90) return "yellow";
  if (hue >= 90 && hue < 150) return "green";
  if (hue >= 150 && hue < 210) return "cyan";
  if (hue >= 210 && hue < 270) return "blue";
  if (hue >= 270 && hue < 330) return "purple";
  return "red";
};

interface TextureCompatibilityResult {
  score: number;
  explanation: string[];
  details: {
    complementaryTextures: boolean;
    contrastLevel: "low" | "medium" | "high";
    textureBalance: "balanced" | "unbalanced";
  };
}

// Define texture types for TypeScript
type Season = "spring" | "summer" | "autumn" | "winter";
type TextureType =
  | "smooth"
  | "knit"
  | "woven"
  | "leather"
  | "suede"
  | "denim"
  | "silk"
  | "linen"
  | "velvet"
  | "metallic"
  | "mesh"
  | "canvas"
  | "rubber";
type TextureRelation = {
  complementary: TextureType[];
  contrasting: TextureType[];
  avoid: TextureType[];
  seasons: Season[];
  seasonStrength: number; // 0-1, how strongly this texture suggests its seasons
};

// Enhanced texture relationships with seasonal associations
const textureRelationships: Record<TextureType, TextureRelation> = {
  smooth: {
    complementary: ["knit", "woven", "leather"],
    contrasting: ["suede", "velvet"],
    avoid: [],
    seasons: ["spring", "summer", "autumn", "winter"],
    seasonStrength: 0.3, // Neutral seasonal association
  },
  knit: {
    complementary: ["smooth", "leather", "denim"],
    contrasting: ["silk", "metallic"],
    avoid: ["mesh"],
    seasons: ["autumn", "winter"],
    seasonStrength: 0.8, // Strong autumn/winter association
  },
  woven: {
    complementary: ["smooth", "leather"],
    contrasting: ["denim", "velvet"],
    avoid: ["mesh"],
    seasons: ["spring", "summer", "autumn"],
    seasonStrength: 0.5,
  },
  leather: {
    complementary: ["smooth", "knit", "woven", "suede"],
    contrasting: ["denim"],
    avoid: ["velvet", "metallic"],
    seasons: ["autumn", "winter"],
    seasonStrength: 0.7,
  },
  suede: {
    complementary: ["smooth", "leather"],
    contrasting: ["denim"],
    avoid: ["silk", "metallic"],
    seasons: ["autumn", "winter"],
    seasonStrength: 0.8,
  },
  denim: {
    complementary: ["knit", "smooth"],
    contrasting: ["leather", "suede"],
    avoid: ["velvet", "silk"],
    seasons: ["spring", "summer", "autumn"],
    seasonStrength: 0.5,
  },
  silk: {
    complementary: ["smooth", "velvet"],
    contrasting: ["knit"],
    avoid: ["denim", "suede"],
    seasons: ["spring", "summer"],
    seasonStrength: 0.7,
  },
  linen: {
    complementary: ["smooth", "canvas"],
    contrasting: ["silk"],
    avoid: ["leather", "velvet"],
    seasons: ["spring", "summer"],
    seasonStrength: 0.9, // Very strong summer association
  },
  velvet: {
    complementary: ["silk", "smooth"],
    contrasting: ["woven"],
    avoid: ["leather", "denim"],
    seasons: ["autumn", "winter"],
    seasonStrength: 0.8,
  },
  metallic: {
    complementary: ["smooth"],
    contrasting: ["knit"],
    avoid: ["leather", "suede"],
    seasons: ["winter"],
    seasonStrength: 0.6,
  },
  mesh: {
    complementary: ["smooth"],
    contrasting: ["canvas"],
    avoid: ["knit", "woven"],
    seasons: ["summer"],
    seasonStrength: 0.7,
  },
  canvas: {
    complementary: ["smooth", "linen"],
    contrasting: ["mesh"],
    avoid: ["velvet"],
    seasons: ["spring", "summer"],
    seasonStrength: 0.6,
  },
  rubber: {
    complementary: ["smooth", "canvas"],
    contrasting: ["mesh"],
    avoid: ["silk", "velvet"],
    seasons: ["spring", "summer", "autumn", "winter"],
    seasonStrength: 0.3,
  },
};

interface SeasonalCompatibilityResult {
  score: number;
  explanation: string[];
  recommendedSeasons: Season[];
  confidence: number;
}

// Helper function to check if a string is a valid TextureType
const isValidTextureType = (texture: string): texture is TextureType => {
  return Object.keys(textureRelationships).includes(texture);
};

export const getTextureSeasonalScore = (
  texture: string,
  color: string,
  season: Season
): SeasonalCompatibilityResult => {
  // Normalize and validate the texture type
  const normalizedTexture = texture.toLowerCase();
  const textureType: TextureType = (
    isValidTextureType(normalizedTexture) ? normalizedTexture : "smooth"
  ) as TextureType;

  // Now TypeScript knows textureType is definitely a TextureType
  const textureInfo = textureRelationships[textureType];

  const hsl = hexToHSL(color);

  // Color-based seasonal scoring
  const colorSeasonScore = getColorSeasonScore(hsl, season);

  // Texture-based seasonal scoring
  const isSeasonalTexture = textureInfo.seasons.includes(season);
  const textureSeasonScore = isSeasonalTexture
    ? textureInfo.seasonStrength
    : 0.3;

  // Combined score with texture having slightly less weight than color
  const combinedScore = colorSeasonScore * 0.6 + textureSeasonScore * 0.4;

  const explanation: string[] = [];
  if (isSeasonalTexture) {
    explanation.push(`${texture} is typical for ${season}`);
  }
  if (textureInfo.seasonStrength > 0.7) {
    explanation.push(
      `${texture} strongly suggests ${textureInfo.seasons.join("/")}`
    );
  }

  return {
    score: combinedScore,
    explanation,
    recommendedSeasons: textureInfo.seasons,
    confidence: textureInfo.seasonStrength,
  };
};

// Helper function to get color-season score
const getColorSeasonScore = (
  hsl: { h: number; s: number; l: number },
  season: Season
): number => {
  // Spring: Fresh, warm, clear colors
  if (season === "spring") {
    if (hsl.s >= 50 && hsl.l >= 60) return 0.9; // Bright, clear colors
    if (hsl.h >= 45 && hsl.h <= 150) return 0.8; // Warm greens and yellows
    return 0.5;
  }

  // Summer: Soft, cool, muted colors
  if (season === "summer") {
    if (hsl.s <= 60 && hsl.l >= 70) return 0.9; // Soft, light colors
    if (hsl.h >= 180 && hsl.h <= 300) return 0.8; // Cool blues and purples
    return 0.5;
  }

  // Autumn: Rich, warm, muted colors
  if (season === "autumn") {
    if (hsl.s >= 40 && hsl.l <= 60) return 0.9; // Rich, deep colors
    if ((hsl.h >= 20 && hsl.h <= 60) || (hsl.h >= 0 && hsl.h <= 15)) return 0.8; // Warm browns and oranges
    return 0.5;
  }

  // Winter: Clear, cool, intense colors
  if (season === "winter") {
    if (hsl.s >= 70 || hsl.l <= 30) return 0.9; // Intense or dark colors
    if (hsl.h >= 180 && hsl.h <= 270) return 0.8; // Cool blues
    return 0.5;
  }

  return 0.5; // Default score
};

export const getTextureCompatibilityScore = (
  texture1: string,
  texture2: string
): TextureCompatibilityResult => {
  // Normalize texture names to lowercase and validate
  const t1 = texture1.toLowerCase();
  const t2 = texture2.toLowerCase();

  // Cast to TextureType if valid, otherwise use 'smooth' as fallback
  const t1Type: TextureType = isValidTextureType(t1)
    ? (t1 as TextureType)
    : "smooth";
  const t2Type: TextureType = isValidTextureType(t2)
    ? (t2 as TextureType)
    : "smooth";

  // Get texture relationships using properly typed keys
  const t1Relations = textureRelationships[t1Type];
  const t2Relations = textureRelationships[t2Type];

  // If same texture, return high score
  if (t1Type === t2Type) {
    return {
      score: 0.9,
      explanation: ["Matching textures create a cohesive look"],
      details: {
        complementaryTextures: true,
        contrastLevel: "low",
        textureBalance: "balanced",
      },
    };
  }

  // Check for complementary textures
  const areComplementary =
    t1Relations.complementary.includes(t2Type) ||
    t2Relations.complementary.includes(t1Type);

  // Check for contrasting textures
  const areContrasting =
    t1Relations.contrasting.includes(t2Type) ||
    t2Relations.contrasting.includes(t1Type);

  // Check for textures to avoid
  const shouldAvoid =
    t1Relations.avoid.includes(t2Type) || t2Relations.avoid.includes(t1Type);

  let score = 0.5; // Start with neutral score
  const explanation: string[] = [];

  if (shouldAvoid) {
    score = 0.3;
    explanation.push("These textures typically clash with each other");
  } else if (areComplementary) {
    score = 0.9;
    explanation.push("These textures complement each other well");
  } else if (areContrasting) {
    score = 0.8;
    explanation.push("These textures create an interesting contrast");
  } else {
    score = 0.7;
    explanation.push("These textures work together adequately");
  }

  return {
    score,
    explanation,
    details: {
      complementaryTextures: areComplementary,
      contrastLevel: areContrasting
        ? "high"
        : areComplementary
        ? "medium"
        : "low",
      textureBalance: shouldAvoid ? "unbalanced" : "balanced",
    },
  };
};

// Function to get overall texture compatibility for an outfit
export const getOutfitTextureScore = (
  textures: string[]
): TextureCompatibilityResult => {
  if (textures.length < 2) {
    return {
      score: 1,
      explanation: ["Single texture outfit"],
      details: {
        complementaryTextures: true,
        contrastLevel: "low",
        textureBalance: "balanced",
      },
    };
  }

  let totalScore = 0;
  let totalPairs = 0;
  const allExplanations = new Set<string>();
  let hasComplementary = false;
  let maxContrast: "low" | "medium" | "high" = "low";

  // Compare each texture pair
  for (let i = 0; i < textures.length; i++) {
    for (let j = i + 1; j < textures.length; j++) {
      const result = getTextureCompatibilityScore(textures[i], textures[j]);
      totalScore += result.score;
      totalPairs++;

      result.explanation.forEach((exp) => allExplanations.add(exp));

      if (result.details.complementaryTextures) hasComplementary = true;
      if (result.details.contrastLevel === "high") maxContrast = "high";
      else if (
        result.details.contrastLevel === "medium" &&
        maxContrast === "low"
      )
        maxContrast = "medium";
    }
  }

  const finalScore = totalPairs > 0 ? totalScore / totalPairs : 1;

  return {
    score: finalScore,
    explanation: Array.from(allExplanations),
    details: {
      complementaryTextures: hasComplementary,
      contrastLevel: maxContrast,
      textureBalance: finalScore >= 0.7 ? "balanced" : "unbalanced",
    },
  };
};

// Define a type for the scoring result
export interface ColorCompatibilityResult {
  score: number;
  explanation: string[];
  details: {
    baseScore: number;
    totalPairs: number;
    penalties: {
      type: string;
      impact: number;
      description: string;
    }[];
  };
}

// Helper function to determine if an item is a major garment (top/bottom)
const isMajorGarment = (type?: string): boolean => {
  if (!type) return false;
  const majorTypes = new Set([
    "shirt",
    "top",
    "blouse",
    "sweater",
    "jacket",
    "pants",
    "trousers",
    "skirt",
    "shorts",
    "jeans",
    "dress", // dress counts as both top and bottom
  ]);
  return majorTypes.has(type.toLowerCase());
};

// Helper function to get garment category
const getGarmentCategory = (
  type?: string
): "top" | "bottom" | "dress" | "accessory" => {
  if (!type) return "accessory";
  type = type.toLowerCase();

  // Direct matches for ClothingType enum values
  if (type === "top") return "top";
  if (type === "bottom") return "bottom";
  if (type === "dress") return "dress";
  if (type === "outerwear") return "top"; // Outerwear counts as a top for color matching
  if (type === "shoes" || type === "accessory") return "accessory";

  // Legacy/detailed type mappings
  const tops = new Set(["shirt", "blouse", "sweater", "jacket"]);
  if (tops.has(type)) return "top";

  const bottoms = new Set(["pants", "trousers", "skirt", "shorts", "jeans"]);
  if (bottoms.has(type)) return "bottom";

  return "accessory";
};

// Helper function to check if two items form a major pair (top-bottom)
const isMajorPair = (type1?: string, type2?: string): boolean => {
  if (!type1 || !type2) return false;
  const cat1 = getGarmentCategory(type1);
  const cat2 = getGarmentCategory(type2);

  // Dress counts as both but shouldn't be paired with itself
  if (cat1 === "dress" && cat2 === "dress") return false;

  // Check for top-bottom pairs
  return (
    (cat1 === "top" && cat2 === "bottom") ||
    (cat1 === "bottom" && cat2 === "top") ||
    // Dress pairs with either top or bottom (but with reduced weight)
    (cat1 === "dress" && (cat2 === "top" || cat2 === "bottom")) ||
    (cat2 === "dress" && (cat1 === "top" || cat1 === "bottom"))
  );
};

// Test function to verify black and white combinations
export const testBlackWhiteCompatibility = () => {
  // Test with pure black and white
  const blackColor = "#000000";
  const whiteColor = "#FFFFFF";

  const result = getColorCompatibilityScore(
    [blackColor],
    [whiteColor],
    [{ type: "shirt" }, { type: "pants" }]
  );

  return result;
};

// Test function to verify the fix is working
export const runColorCompatibilityTests = () => {
  // Test 1: Black shirt + White pants (should be high score)
  const test1 = getColorCompatibilityScore(
    ["#000000"],
    ["#FFFFFF"],
    [{ type: "shirt" }, { type: "pants" }]
  );

  // Test 2: Black shirt + Black pants (should be low score)
  const test2 = getColorCompatibilityScore(
    ["#000000"],
    ["#000000"],
    [{ type: "shirt" }, { type: "pants" }]
  );

  // Test 3: White shirt + White pants (should be low score)
  const test3 = getColorCompatibilityScore(
    ["#FFFFFF"],
    ["#FFFFFF"],
    [{ type: "shirt" }, { type: "pants" }]
  );

  // Test 4: Black shirt + Gray pants (should be medium score)
  const test4 = getColorCompatibilityScore(
    ["#000000"],
    ["#808080"],
    [{ type: "shirt" }, { type: "pants" }]
  );

  return { test1, test2, test3, test4 };
};

// Test function with real-world colors from user's outfit
export const testRealWorldColors = () => {
  // Test with the exact colors from the user's outfit
  const selectedColors = ["#282828", "#202020", "#e8e8e7"]; // top/bottom colors
  const itemColors = ["#c4c4cc", "#dedee5", "#9e9ea7"]; // shoes colors

  // Test top/bottom colors against each other
  const result1 = getColorCompatibilityScore(
    [selectedColors[0]], // #282828 (dark gray)
    [selectedColors[2]], // #e8e8e7 (light gray)
    [{ type: "top" }, { type: "bottom" }]
  );

  return result1;
};
